import {
  Button,
  Flag,
  ModalActions,
  ModalContent,
  ModalHeader,
  SegControl,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { CurrencyInputWithSymbol } from '@vend/business-components'
import { ApiProduct, GiftCard } from '@vend/types'
import { DATE_MEDIUM, formatDateTime } from '@vend/utilities'
import uuid from '@vend/vend-uuid'
import React, { useState } from 'react'
import { OmniGiftCardSettings } from '../../../controllers/gift-cards/balances-gift-cards'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import { useFormatCurrencyValue } from '../../sale/summary-panel/useFormatCurrencyValue'
import {
  useDisplayStatus,
  useGiftCardProviderSettings,
  useSetValueStrings,
} from '../hooks'
import {
  GiftCardModalFlow,
  GiftCardModalState,
  GiftCardType,
} from './GiftCardModal'

interface Props {
  flow: GiftCardModalFlow
  state: GiftCardModalState
  giftCard: GiftCard
  giftCardType: GiftCardType
  giftCardSettings: OmniGiftCardSettings | null
  product: ApiProduct
  onClose: () => void
}

export const SetValue: React.FC<Props> = ({
  flow,
  state,
  giftCard,
  giftCardType,
  giftCardSettings,
  product,
  onClose,
}) => {
  const t = useT()
  const [predefinedIndex, setPredefinedIndex] = useState<number | null>(null)
  const [amount, setAmount] = useState<number | string>('')
  const [hasError, setHasError] = useState(false)

  const currentOutlet = useRegisterManager().getActiveOutlet()
  const saleManager = useSaleManager()
  const formatCurrencyValue = useFormatCurrencyValue()

  const { header } = useSetValueStrings(state, giftCardType)
  const { giftCardCodeCapitalised } = useGiftCardProviderSettings(
    flow,
    giftCardType
  )

  const minCustomAmount = giftCardSettings?.minValue
  const maxCustomAmount = giftCardSettings?.maxValue

  const customAmountErrorMessages = {
    numbersOnly: t('Amount can only contain numbers', {
      _context: 'Custom gift card amount non numeric validation message',
    }),
    min: t('Amount must be more than {minAmount}', {
      _context: 'Validation Message',
      minAmount: minCustomAmount,
    }),
    max: t('Amount must be less than {maxAmount}', {
      _context: 'Validation Message',
      maxAmount: maxCustomAmount,
    }),
  }

  const handlePredefinedOptionChange = (
    index: number | null,
    amount: number | string
  ) => {
    setPredefinedIndex(index)
    setAmount(amount)
    setHasError(false)
  }

  // Disable form if there is an error or custom amount is empty
  const disabled = hasError || amount === ''
  const displayStatus = useDisplayStatus(giftCard.status)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saleManager.addGiftCardItem({
      product,
      code: giftCard.code,
      balance: amount,
      expiresAt: giftCard.expiresAt,
      isReload: state === 'ADD_BALANCE',
      clientId: uuid(),
    })

    onClose()
  }

  return (
    <form onSubmit={e => handleSubmit(e)}>
      <ModalHeader>{header}</ModalHeader>

      <ModalContent>
        <div className="vd-mb8 wr-gift-card-info-fields">
          <div className="wr-gift-card-container">
            <div className="wr-gift-card-column">
              <div className="wr-info-field-label vd-mb1">
                {giftCardCodeCapitalised}
              </div>
              <div className="wr-info-field-value wr-gift-cards-number-number-status wr-gift-card-code">
                <span className="wr-gift-cards-number">{giftCard.code}</span>
                <Flag content={displayStatus} className="vd-mr2" />
              </div>
            </div>

            {!!giftCard.expiresAt && (
              <div className="wr-gift-card-column">
                <div className="wr-info-field-label vd-mb1">
                  <T
                    _str="Expiry date"
                    _context="Gift card expiry date label"
                  />
                </div>
                <div>
                  <span className="wr-info-field-value">
                    {formatDateTime(giftCard.expiresAt, {
                      ...DATE_MEDIUM,
                      timeZone: currentOutlet.time_zone,
                    })}
                  </span>
                </div>
              </div>
            )}

            {state === 'ADD_BALANCE' ? (
              <div className="wr-gift-card-column">
                <div className="wr-info-field-label vd-mb1">
                  <T
                    _str="Current balance"
                    _context="Gift card current balance label"
                  />
                </div>
                <div>
                  <span className="wr-info-field-value">
                    {formatCurrencyValue(giftCard.balance)}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {giftCardSettings && giftCardSettings.predefined_amounts.length > 0 ? (
          <div className="vd-label vd-mb4">
            {state === 'SET_VALUE' && (
              <T
                _str="Select the amount"
                _context="Select a gift card value label"
                type="label"
              />
            )}
            {state === 'ADD_BALANCE' && (
              <T
                _str="Select an amount to add to the current balance"
                _context="Add gift card value label"
                type="label"
              />
            )}
          </div>
        ) : null}

        {giftCardSettings && giftCardSettings.predefined_amounts.length > 0 ? (
          <div className="vd-flex vd-mb8">
            <SegControl
              name="custom"
              className="vd-segcontrol--stretched"
              checked={predefinedIndex === null}
              onChange={() => handlePredefinedOptionChange(null, '')}
              data-testid="cancellation-put-data-on-ice-radio-selector"
              data-track="custom-amount"
            >
              <T
                _str="Custom"
                _context="Custom amount option in the set value modal"
              />
            </SegControl>
            {giftCardSettings.predefined_amounts.map((amount, index) => (
              <SegControl
                key={index}
                name={`gift card ${amount} value select`}
                className="vd-segcontrol--stretched"
                checked={predefinedIndex === index}
                onChange={() => handlePredefinedOptionChange(index, amount)}
                data-track="predefined-amount"
              >
                {`${currentOutlet.currency_symbol}${amount}`}
              </SegControl>
            ))}
          </div>
        ) : null}

        {predefinedIndex === null ? (
          <>
            <div className="vd-label vd-mb1">
              <T
                _str="Enter amount"
                _context="Select a gift card value label"
                type="label"
              />
            </div>
            <CurrencyInputWithSymbol
              value={''}
              onChange={amount => {
                setHasError(false)
                setAmount(amount)
              }}
              errorMessages={customAmountErrorMessages}
              min={minCustomAmount}
              max={maxCustomAmount}
              className="vd-g-s-6"
              placeholder="0.00"
              autoFocus
              onError={e => {
                setHasError(true)
                setAmount(e.target.value)
              }}
            />
          </>
        ) : null}
      </ModalContent>

      <ModalActions className="vd-btn-group">
        <Button
          data-track="cancel-set-value"
          variant="supplementary"
          name="Cancel"
          onClick={onClose}
        >
          <T _str="Cancel" _context="Cancel button in the set value modal" />
        </Button>
        <Button
          variant="go"
          name="Add to sale"
          type="submit"
          disabled={disabled}
          data-track="add-to-sale"
        >
          <T
            _str="Add to sale"
            _context="Add to sale button in the set value modal"
          />
        </Button>
      </ModalActions>
    </form>
  )
}
